import { useEffect, useRef, useState, useId, useCallback, type ReactNode } from 'react';
import { 
    type BaseAsset, 
    type EventHandler, 
    type IEngineInitializer,
    UIBridgeSubsystem,
    VisionClient,
    VisionEvents // ## Import VisionEvents for listening
} from '@akavirtual/deai-web-core';
import { useVisionManager } from '@/context/vision-manager-provider';
import { VisionCanvasContext } from '@/context/vision-canvas-context';

type VisionCanvasProps = {
    children?: ReactNode;
    instanceId?: string;
    assetBaseUrl?: string;
    definition: BaseAsset | null;
    className?: string;
    onInitialized?: (id: string) => void;
    initializer?: new () => IEngineInitializer;
};

export const VisionCanvas = ({
    children,
    instanceId,
    assetBaseUrl = '',
    definition,
    className,
    onInitialized,
    initializer,
}: VisionCanvasProps) => {
    const manager = useVisionManager();
    const autoId = useId();
    const id = instanceId || autoId;

    const [visionClient, setVisionClient] = useState<VisionClient | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    
    // ## State changed from isModelLoaded to isGameReady
    const [isGameReady, setIsGameReady] = useState(false);
    
    const [resetKey, setResetKey] = useState(0);
    
    // ## The reset function now also sets isGameReady to false
    const reset = useCallback(() => {
        console.log(`[VisionCanvas ${id}] Reset triggered.`);
        setIsGameReady(false); 
        setResetKey(prevKey => prevKey + 1); 
    }, [id]);

    // This effect handles the CREATION and DESTRUCTION of the VisionClient. (Unchanged)
    useEffect(() => {
        console.log(`[VisionCanvas ${id}] Creating VisionClient instance.`);
        const client = manager.createClient(id, { assetBaseUrl }, initializer);
        setVisionClient(client);

        return () => {
            console.log(`[VisionCanvas ${id}] Destroying VisionClient instance.`);
            manager.destroyClient(id);
            setVisionClient(null);
        };
    }, [id, manager, assetBaseUrl, initializer]);

    // This effect handles SCENE LOADING. (Now simplified)
    useEffect(() => {
        if (visionClient && canvasRef.current && definition) {
            visionClient.setCanvas(canvasRef.current);
            visionClient.enterScene(definition)
                .then(() => {
                    console.log(`[VisionCanvas ${id}] Scene loaded successfully. Waiting for GAME_READY event.`);
                    onInitialized?.(id);
                })
                .catch(error => console.error(`[VisionCanvas ${id}] Failed to load scene:`, error));
        }
    }, [visionClient, definition, onInitialized, id, resetKey]);

    // ## NEW: This effect listens for game state events from the UIBridge.
    useEffect(() => {
        if (!visionClient) return;

        const uiBridge = visionClient.getSubsystem<UIBridgeSubsystem>(UIBridgeSubsystem.ID);
        if (!uiBridge) return;

        const handleGameReady = () => {
            console.log(`[VisionCanvas ${id}] Received GAME_READY event.`);
            setIsGameReady(true);
        };
        
        // The reset callback already handles setting state, so we just call it.
        const handleGameReset = () => {
             console.log(`[VisionCanvas ${id}] Received GAME_RESET event.`);
             reset();
        };

        uiBridge.on(VisionEvents.GAME_READY, handleGameReady);
        uiBridge.on(VisionEvents.GAME_RESET, handleGameReset);

        // Cleanup listeners when the client changes or component unmounts
        return () => {
            uiBridge.off(VisionEvents.GAME_READY, handleGameReady);
            uiBridge.off(VisionEvents.GAME_RESET, handleGameReset);
        };
    }, [visionClient, reset, id]); // Depend on the client and reset function

    // API callbacks are unchanged
    const invoke = useCallback(async (type: string, payload: { entityName: string, [key: string]: any }): Promise<any> => {
        const uiBridge = visionClient?.getSubsystem<UIBridgeSubsystem>(UIBridgeSubsystem.ID);
        if (uiBridge) return uiBridge.invoke(type, payload);
    }, [visionClient]);

    const queryData = useCallback((payload: { entityName: string, componentName: string, propertyName: string }): any => {
        const uiBridge = visionClient?.getSubsystem<UIBridgeSubsystem>(UIBridgeSubsystem.ID);
        if (uiBridge) return uiBridge.queryData(payload);
    }, [visionClient]);

    const broadcast = useCallback((type: string, payload?: any): void => {
        const uiBridge = visionClient?.getSubsystem<UIBridgeSubsystem>(UIBridgeSubsystem.ID);
        if (uiBridge) uiBridge.emit(type, payload);
    }, [visionClient]);

    const listenOn = useCallback((eventType: string, handler: EventHandler): void => {
        const uiBridge = visionClient?.getSubsystem<UIBridgeSubsystem>(UIBridgeSubsystem.ID);
        if (uiBridge) uiBridge.on(eventType, handler);
    }, [visionClient]);

    const listenOff = useCallback((eventType: string, handler: EventHandler): void => {
        const uiBridge = visionClient?.getSubsystem<UIBridgeSubsystem>(UIBridgeSubsystem.ID);
        if (uiBridge) uiBridge.off(eventType, handler);
    }, [visionClient]);

    // ## Update the value provided by the context
    const value = {
        client: visionClient,
        invoke,
        queryData,
        broadcast,
        listenOn,
        listenOff,
        isGameReady,
        reset
    };

    return (
        <VisionCanvasContext.Provider value={value}>
            <div className={className} style={{ width: '100%', height: '100%', position: 'relative' }}>
                <div ref={canvasRef} style={{ width: '100%', height: '100%', position: 'absolute' }} />
                {/* ## Conditionally render children based on isGameReady */}
                {children}
            </div>
        </VisionCanvasContext.Provider>
    );
};
